import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_BUSINESS_TIME_ZONE,
  businessDateTimeToDate,
  formatAppointmentTime,
  toBusinessDateKey,
} from "@zapis24/utils";
import { apiFetch, fmtTime, toLocalDateStr } from "../api.js";
import {
  buildManualAppointmentPayload,
  EMPTY_MANUAL_APPOINTMENT_FORM,
  filterFutureSlotsForDate,
  getManualAppointmentValidationError,
  type ManualAppointmentFormState,
} from "../manualAppointment.js";
import {
  buildCompleteAppointmentPatch,
  getAppointmentActionAvailability,
  getAppointmentCardDisclosureState,
  getAppointmentDaySummary,
  getHiddenAppointments,
  getMasterVisibleAppointmentStatusMeta,
  getReturnReminderDateValue,
  getVisibleAppointments,
  selectScheduleServiceId,
  type AdminAppointmentStatus,
} from "../appointmentsView.js";
import { buildScheduleCalendarItems } from "../scheduleCalendar.js";
import { ReturnReminderDaysPicker } from "../ReturnReminderDaysPicker.js";

interface Service { id: string; name: string; durationMin: number; priceFrom: number; priceTo: number | null; isActive?: boolean }
interface Client { id: string; name: string; phone: string | null }
interface Appointment {
  id: string;
  startsAt: string;
  endsAt: string;
  status: AdminAppointmentStatus;
  notes: string | null;
  source: string;
  actualPrice: number | null;
  client: Client;
  service: Service;
}
interface Slot { startsAt: string; endsAt: string; timeStr: string }

const SOURCE_LABEL: Record<string, string> = {
  TELEGRAM: "TG", MAX: "MAX", VK: "VK", LANDING: "Сайт", DIRECT: "Напрямую", ADMIN_PANEL: "Админка",
};

function fmtPrice(kopecks: number): string {
  return Math.round(kopecks / 100).toLocaleString("ru-RU") + " ₽";
}

function priceRange(svc: Service): string {
  const from = Math.round(svc.priceFrom / 100);
  const to = svc.priceTo ? Math.round(svc.priceTo / 100) : null;
  if (to && to !== from) return `${from.toLocaleString("ru-RU")} – ${to.toLocaleString("ru-RU")} ₽`;
  return `${from.toLocaleString("ru-RU")} ₽`;
}

function buildWeekStrip(centerDate: string): { dateStr: string; dayNum: number; weekday: string }[] {
  const center = new Date(centerDate + "T00:00:00");
  const days = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(center);
    d.setDate(center.getDate() + i);
    days.push({
      dateStr: toLocalDateStr(d),
      dayNum: d.getDate(),
      weekday: d.toLocaleDateString("ru-RU", { weekday: "short" }).replace(".", ""),
    });
  }
  return days;
}

export function AppointmentsPage({
  token,
  initialDate,
  focusAppointmentId,
  focusRequestId,
}: {
  token: string;
  initialDate?: string | undefined;
  focusAppointmentId?: string | undefined;
  focusRequestId?: number | undefined;
}) {
  const [selectedDate, setSelectedDate] = useState(() => initialDate ?? toLocalDateStr(new Date()));
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState<string | null>(
    () => focusAppointmentId ?? null,
  );
  const focusedAppointmentRef = useRef<HTMLDivElement | null>(null);
  const createFormRef = useRef<HTMLFormElement | null>(null);
  const businessTodayAppliedRef = useRef(false);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<ManualAppointmentFormState>(() => ({
    ...EMPTY_MANUAL_APPOINTMENT_FORM,
    date: toLocalDateStr(new Date()),
  }));
  const [createError, setCreateError] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedScheduleServiceId, setSelectedScheduleServiceId] = useState("");
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [actualPriceInput, setActualPriceInput] = useState("");
  const [returnReminderDaysInput, setReturnReminderDaysInput] = useState("");
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [noShowConfirmId, setNoShowConfirmId] = useState<string | null>(null);
  const [reschedulingAppt, setReschedulingAppt] = useState<Appointment | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyResult, setNotifyResult] = useState<"ok" | "no_channel" | null>(null);
  const [returnReminderAppt, setReturnReminderAppt] = useState<Appointment | null>(null);
  const [returnReminderDate, setReturnReminderDate] = useState("");
  const [expandedAppointmentIds, setExpandedAppointmentIds] = useState<Set<string>>(
    () => new Set(focusAppointmentId ? [focusAppointmentId] : []),
  );

  const qc = useQueryClient();

  const closeAll = useCallback(() => {
    setCompletingId(null);
    setCancelConfirmId(null);
    setNoShowConfirmId(null);
    setReschedulingAppt(null);
    setNotifyingId(null);
    setNotifyResult(null);
    setReturnReminderDaysInput("");
    setReturnReminderAppt(null);
    setReturnReminderDate("");
  }, []);

  useEffect(() => {
    if (!initialDate || !focusAppointmentId) return;

    setSelectedDate(initialDate);
    setHighlightedAppointmentId(focusAppointmentId);
    setExpandedAppointmentIds(new Set([focusAppointmentId]));
    setShowCreateForm(false);
    closeAll();
  }, [closeAll, focusAppointmentId, focusRequestId, initialDate]);

  // businessId нужен для запроса слотов (публичный эндпоинт)
  const { data: bizData } = useQuery({
    queryKey: ["business-me"],
    queryFn: () =>
      apiFetch<{ id: string; name: string; brandColor: string | null; timeZone: string }>(
        token,
        "/api/business/me",
      ),
    staleTime: 300_000,
  });
  const loadedBusinessTimeZone = bizData?.timeZone;
  const businessTimeZone = loadedBusinessTimeZone ?? DEFAULT_BUSINESS_TIME_ZONE;

  useEffect(() => {
    if (!loadedBusinessTimeZone || initialDate || businessTodayAppliedRef.current) return;
    businessTodayAppliedRef.current = true;
    const browserToday = toLocalDateStr(new Date());
    const businessToday = toBusinessDateKey(new Date(), loadedBusinessTimeZone);
    setSelectedDate((current) => (current === browserToday ? businessToday : current));
    setCreateForm((form) => (form.date === browserToday ? { ...form, date: businessToday } : form));
  }, [loadedBusinessTimeZone, initialDate]);

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ["appointments", selectedDate],
    queryFn: () => apiFetch<Appointment[]>(token, `/api/appointments?date=${selectedDate}`),
  });

  useEffect(() => {
    if (!highlightedAppointmentId || isLoading) return;
    if (!appointments.some((appointment) => appointment.id === highlightedAppointmentId)) return;

    focusedAppointmentRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    const timeoutId = window.setTimeout(() => {
      setHighlightedAppointmentId((current) =>
        current === highlightedAppointmentId ? null : current,
      );
    }, 2_500);

    return () => window.clearTimeout(timeoutId);
  }, [appointments, highlightedAppointmentId, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    const availableAppointmentIds = new Set(appointments.map((appointment) => appointment.id));
    setExpandedAppointmentIds((current) => {
      const next = new Set([...current].filter((id) => availableAppointmentIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [appointments, isLoading]);

  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiFetch<Service[]>(token, "/api/services"),
    staleTime: 60_000,
  });

  useEffect(() => {
    const nextScheduleServiceId = selectScheduleServiceId({
      currentServiceId: selectedScheduleServiceId,
      services,
    });

    if (nextScheduleServiceId !== selectedScheduleServiceId) {
      setSelectedScheduleServiceId(nextScheduleServiceId);
    }
  }, [selectedScheduleServiceId, services]);

  const { data: clients = [], isFetching: clientsLoading } = useQuery({
    queryKey: ["clients", "manual-create"],
    queryFn: () => apiFetch<Client[]>(token, "/api/clients?limit=200"),
    enabled: showCreateForm,
    staleTime: 15_000,
  });

  // Слоты для переноса — запрашиваем когда выбрана дата
  const { data: availableSlots = [], isFetching: slotsLoading } = useQuery({
    queryKey: ["reschedule-slots", bizData?.id, reschedulingAppt?.service.id, rescheduleDate],
    queryFn: () =>
      apiFetch<Slot[]>(token, `/api/slots/${bizData!.id}?serviceId=${reschedulingAppt!.service.id}&date=${rescheduleDate}`),
    enabled: !!bizData?.id && !!reschedulingAppt && !!rescheduleDate,
    staleTime: 30_000,
  });

  const { data: createSlots = [], isFetching: createSlotsLoading } = useQuery({
    queryKey: ["create-slots", bizData?.id, createForm.serviceId, createForm.date],
    queryFn: () =>
      apiFetch<Slot[]>(token, `/api/slots/${bizData!.id}?serviceId=${createForm.serviceId}&date=${createForm.date}`),
    enabled: showCreateForm && !!bizData?.id && !!createForm.serviceId && !!createForm.date,
    staleTime: 30_000,
  });

  const { data: scheduleSlots = [], isFetching: scheduleSlotsLoading } = useQuery({
    queryKey: ["schedule-calendar-slots", bizData?.id, selectedScheduleServiceId, selectedDate],
    queryFn: () =>
      apiFetch<Slot[]>(token, `/api/slots/${bizData!.id}?serviceId=${selectedScheduleServiceId}&date=${selectedDate}`),
    enabled: !!bizData?.id && !!selectedScheduleServiceId && !!selectedDate,
    staleTime: 30_000,
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      apiFetch(token, `/api/appointments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments"] });
      void qc.invalidateQueries({ queryKey: ["today-stats"] });
      closeAll();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(token, `/api/appointments/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments"] });
      void qc.invalidateQueries({ queryKey: ["today-stats"] });
      closeAll();
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: ReturnType<typeof buildManualAppointmentPayload>) =>
      apiFetch<Appointment>(token, "/api/appointments", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (appointment) => {
      const appointmentDate = toBusinessDateKey(appointment.startsAt, businessTimeZone);
      setSelectedDate(appointmentDate);
      void qc.invalidateQueries({ queryKey: ["appointments"] });
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["today-stats"] });
      void qc.invalidateQueries({ queryKey: ["business-me"] });
      setShowCreateForm(false);
      setCreateForm({ ...EMPTY_MANUAL_APPOINTMENT_FORM, date: appointmentDate });
      setClientSearch("");
      setCreateError("");
    },
    onError: (e: Error) => setCreateError(e.message),
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ id, startsAt }: { id: string; startsAt: string }) =>
      apiFetch(token, `/api/appointments/${id}`, { method: "PATCH", body: JSON.stringify({ startsAt }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments"] });
      void qc.invalidateQueries({ queryKey: ["today-stats"] });
      closeAll();
    },
  });

  const notifyMutation = useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      apiFetch(token, `/api/appointments/${id}/notify-client`, { method: "POST", body: JSON.stringify({ message }) }),
    onSuccess: () => {
      setNotifyResult("ok");
      setTimeout(() => closeAll(), 2000);
    },
    onError: () => {
      setNotifyResult("no_channel");
    },
  });

  const today = toLocalDateStr(new Date());
  const isToday = selectedDate === today;
  const strip = buildWeekStrip(selectedDate);
  const activeServices = services.filter((s) => s.isActive !== false);
  const selectedScheduleService = activeServices.find((s) => s.id === selectedScheduleServiceId) ?? null;
  const selectedCreateService = activeServices.find((s) => s.id === createForm.serviceId) ?? null;
  const visibleCreateSlots = filterFutureSlotsForDate(createSlots, createForm.date, new Date(), {
    timeZone: businessTimeZone,
  });
  const visibleAvailableSlots = filterFutureSlotsForDate(availableSlots, rescheduleDate, new Date(), {
    timeZone: businessTimeZone,
  });
  const visibleScheduleSlots = filterFutureSlotsForDate(scheduleSlots, selectedDate, new Date(), {
    timeZone: businessTimeZone,
  });
  const clientSearchValue = clientSearch.trim().toLowerCase();
  const clientSearchDigits = clientSearch.replace(/\D/g, "");
  const matchingClients = clients
    .filter((client) => {
      if (!clientSearchValue) return true;
      return (
        client.name.toLowerCase().includes(clientSearchValue) ||
        (clientSearchDigits.length >= 3 && (client.phone ?? "").replace(/\D/g, "").includes(clientSearchDigits))
      );
    })
    .slice(0, 6);

  const visibleAppointments = getVisibleAppointments(appointments);
  const hiddenAppointments = getHiddenAppointments(appointments);
  const daySummary = getAppointmentDaySummary(appointments);
  const scheduleItems = buildScheduleCalendarItems({
    appointments,
    slots: visibleScheduleSlots,
    timeZone: businessTimeZone,
  });

  function startComplete(appt: Appointment) {
    closeAll();
    setActualPriceInput(String(Math.round(appt.service.priceFrom / 100)));
    setReturnReminderDaysInput("");
    setCompletingId(appt.id);
  }

  function toggleAppointmentCard(appointmentId: string) {
    if (expandedAppointmentIds.has(appointmentId)) {
      closeAll();
      setExpandedAppointmentIds((current) => {
        const next = new Set(current);
        next.delete(appointmentId);
        return next;
      });
      return;
    }

    setExpandedAppointmentIds((current) => new Set(current).add(appointmentId));
  }

  function handleAppointmentCardClick(event: React.MouseEvent<HTMLDivElement>, appointmentId: string) {
    if (event.target instanceof HTMLElement) {
      const interactiveTarget = event.target.closest("a,button,input,label,select,textarea");
      if (interactiveTarget) return;
    }

    toggleAppointmentCard(appointmentId);
  }

  function openCreateAppointment(prefill?: { date?: string; time?: string; serviceId?: string }) {
    closeAll();
    const serviceId = prefill?.serviceId || selectedScheduleServiceId || activeServices[0]?.id || "";
    setCreateForm({
      ...EMPTY_MANUAL_APPOINTMENT_FORM,
      date: prefill?.date ?? selectedDate,
      time: prefill?.time ?? "",
      serviceId,
    });
    setClientSearch("");
    setCreateError("");
    setShowCreateForm(true);
    window.setTimeout(() => createFormRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 0);
  }

  function selectExistingClient(client: Client) {
    setCreateForm((form) => ({
      ...form,
      clientMode: "existing",
      clientId: client.id,
      clientName: client.name,
      clientPhone: client.phone ?? "",
    }));
    setClientSearch(client.name);
  }

  function switchClientMode(clientMode: ManualAppointmentFormState["clientMode"]) {
    setCreateForm((form) => ({
      ...form,
      clientMode,
      clientId: clientMode === "new" ? "" : form.clientId,
    }));
    setCreateError("");
  }

  function submitCreateAppointment(e: React.FormEvent) {
    e.preventDefault();
    const validationError = getManualAppointmentValidationError(createForm);
    if (validationError) {
      setCreateError(validationError);
      return;
    }
    createMutation.mutate(buildManualAppointmentPayload(createForm, { timeZone: businessTimeZone }));
  }

  function confirmComplete(appt: Appointment) {
    patchMutation.mutate({
      id: appt.id,
      body: buildCompleteAppointmentPatch({
        appointment: appt,
        actualPriceRubles: actualPriceInput,
        returnReminderDays: returnReminderDaysInput,
        timeZone: businessTimeZone,
      }),
    });
  }

  function startReschedule(appt: Appointment) {
    closeAll();
    setRescheduleDate(toBusinessDateKey(appt.startsAt, businessTimeZone));
    setRescheduleTime(formatAppointmentTime(appt.startsAt, businessTimeZone));
    setReschedulingAppt(appt);
  }

  function confirmReschedule(id: string) {
    const dt = businessDateTimeToDate(rescheduleDate, rescheduleTime, businessTimeZone);
    rescheduleMutation.mutate({ id, startsAt: dt.toISOString() });
  }

  function startNotify(appt: Appointment) {
    closeAll();
    setNotifyMessage(`Напоминаем о вашей записи в ${fmtTime(appt.startsAt, businessTimeZone)}. Вы в пути? 😊`);
    setNotifyingId(appt.id);
  }

  function startReturnReminder(appt: Appointment) {
    closeAll();
    setReturnReminderAppt(appt);
    setReturnReminderDate(getReturnReminderDateValue(appt, 30, businessTimeZone));
  }

  function setReturnReminderAfter(appt: Appointment, days: number) {
    setReturnReminderDate(getReturnReminderDateValue(appt, days, businessTimeZone));
  }

  function confirmReturnReminder(id: string) {
    if (!returnReminderDate) return;
    patchMutation.mutate({
      id,
      body: {
        returnReminderAt: businessDateTimeToDate(returnReminderDate, "10:00", businessTimeZone).toISOString(),
      },
    });
  }

  return (
    <div className="mx-auto w-full max-w-2xl min-w-0 overflow-x-hidden">
      {/* 7-day strip */}
      <div className="sticky top-0 z-[5] w-full min-w-0 border-b border-gray-100 bg-white px-4 pb-3 pt-4">
        <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase text-gray-400 tracking-wide">Расписание</p>
          <button
            onClick={() => openCreateAppointment()}
            className="shrink-0 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90 transition"
          >
            Добавить запись
          </button>
        </div>

        <div className="mb-3 flex min-w-0 gap-1">
          {strip.map(({ dateStr, dayNum, weekday }) => {
            const isSelected = dateStr === selectedDate;
            const isTodayDay = dateStr === today;
            return (
              <button
                key={dateStr}
                onClick={() => setSelectedDate(dateStr)}
                className={`flex min-w-0 flex-1 flex-col items-center rounded-xl py-1.5 transition ${
                  isSelected ? "bg-primary text-white" : "hover:bg-gray-50 text-gray-600"
                }`}
              >
                <span className={`text-[10px] font-medium uppercase ${isSelected ? "text-white/80" : "text-gray-400"}`}>
                  {weekday}
                </span>
                <span className={`text-base font-bold leading-tight ${isSelected ? "text-white" : isTodayDay ? "text-primary" : "text-gray-800"}`}>
                  {dayNum}
                </span>
                {isTodayDay && !isSelected && <span className="w-1 h-1 rounded-full bg-primary mt-0.5" />}
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 items-center justify-between gap-2">
          <button onClick={() => { const d = new Date(selectedDate + "T00:00:00"); d.setDate(d.getDate() - 7); setSelectedDate(toLocalDateStr(d)); }} className="text-gray-400 hover:text-gray-600 px-1 py-0.5 text-xs">← −7д</button>
          <p className="min-w-0 truncate text-center text-sm font-semibold text-gray-800">
            {isToday ? "Сегодня" : new Date(selectedDate + "T00:00:00").toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <button onClick={() => { const d = new Date(selectedDate + "T00:00:00"); d.setDate(d.getDate() + 7); setSelectedDate(toLocalDateStr(d)); }} className="text-gray-400 hover:text-gray-600 px-1 py-0.5 text-xs">+7д →</button>
        </div>

        {/* Day summary — inside sticky strip so always visible */}
        {appointments.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2 border-t border-gray-100 mt-1">
            <div className="text-center">
              <span className="text-base font-bold text-gray-800">{daySummary.visibleCount}</span>
              <span className="text-[11px] text-gray-400 ml-1">записей</span>
            </div>
            <span className="text-gray-200">·</span>
            <div className="text-center">
              <span className="text-base font-bold text-primary">{daySummary.activeCount}</span>
              <span className="text-[11px] text-gray-400 ml-1">активных</span>
            </div>
            <span className="text-gray-200">·</span>
            <div className="text-center">
              <span className="text-base font-bold text-purple-600">{daySummary.inProgressCount}</span>
              <span className="text-[11px] text-gray-400 ml-1">сейчас</span>
            </div>
            <span className="text-gray-200">·</span>
            <div className="text-center">
              <span className="text-base font-bold text-green-600">
                {daySummary.completedCount > 0 ? fmtPrice(daySummary.revenueKopecks) : "—"}
              </span>
              <span className="text-[11px] text-gray-400 ml-1">выручка</span>
            </div>
            {daySummary.hiddenCount > 0 && (
              <>
                <span className="text-gray-200">·</span>
                <div className="text-center">
                  <span className="text-base font-bold text-gray-400">{daySummary.hiddenCount}</span>
                  <span className="text-[11px] text-gray-400 ml-1">скрыто</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="min-w-0 space-y-3 p-4">
        {showCreateForm && (
          <form ref={createFormRef} onSubmit={submitCreateAppointment} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-gray-800">Новая запись</h2>
                <p className="text-xs text-gray-400 mt-0.5">Админка · ручное создание</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError("");
                }}
                className="w-8 h-8 rounded-full bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Услуга *</label>
              <select
                required
                value={createForm.serviceId}
                onChange={(e) => setCreateForm((form) => ({ ...form, serviceId: e.target.value, time: "" }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
              >
                <option value="">Выберите услугу</option>
                {activeServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {selectedCreateService && (
                <p className="text-[11px] text-gray-400 mt-1">
                  {selectedCreateService.durationMin} мин · {priceRange(selectedCreateService)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Дата *</label>
                <input
                  required
                  type="date"
                  value={createForm.date}
                  onChange={(e) => setCreateForm((form) => ({ ...form, date: e.target.value, time: "" }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Время *</label>
                <input
                  required
                  type="time"
                  value={createForm.time}
                  onChange={(e) => setCreateForm((form) => ({ ...form, time: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
                />
              </div>
            </div>

            {createForm.serviceId && createForm.date && (
              <div>
                {createSlotsLoading ? (
                  <p className="text-xs text-gray-400">Загрузка слотов...</p>
                ) : visibleCreateSlots.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {visibleCreateSlots.slice(0, 18).map((slot) => (
                      <button
                        key={slot.startsAt}
                        type="button"
                        onClick={() => setCreateForm((form) => ({ ...form, time: slot.timeStr }))}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border transition font-medium ${
                          createForm.time === slot.timeStr
                            ? "bg-primary border-primary text-white"
                            : "bg-gray-50 border-gray-100 text-gray-600 hover:border-primary/30 hover:bg-primary/5"
                        }`}
                      >
                        {slot.timeStr}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-3 py-2">Свободных слотов на этот день нет</p>
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Клиент *</label>
              <div className="grid grid-cols-2 gap-1.5 mb-3 rounded-xl bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => switchClientMode("new")}
                  className={`rounded-lg py-2 text-xs font-semibold transition ${
                    createForm.clientMode === "new" ? "bg-white text-gray-800 shadow-sm" : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Новый
                </button>
                <button
                  type="button"
                  onClick={() => switchClientMode("existing")}
                  className={`rounded-lg py-2 text-xs font-semibold transition ${
                    createForm.clientMode === "existing" ? "bg-white text-gray-800 shadow-sm" : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Из базы
                </button>
              </div>

              {createForm.clientMode === "new" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    required
                    value={createForm.clientName}
                    onChange={(e) => setCreateForm((form) => ({ ...form, clientName: e.target.value }))}
                    placeholder="Имя клиента"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
                  />
                  <input
                    required
                    value={createForm.clientPhone}
                    onChange={(e) => setCreateForm((form) => ({ ...form, clientPhone: e.target.value }))}
                    placeholder="+7 900 123-45-67"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Поиск по имени или телефону"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary/50"
                  />
                  {createForm.clientId && (
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/5 border border-primary/10 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{createForm.clientName}</p>
                        <p className="text-xs text-gray-400 truncate">{createForm.clientPhone || "телефон не указан"}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCreateForm((form) => ({ ...form, clientId: "", clientName: "", clientPhone: "" }))}
                        className="text-xs text-primary font-medium"
                      >
                        Сменить
                      </button>
                    </div>
                  )}
                  {!createForm.clientId && (
                    <div className="max-h-48 overflow-auto rounded-xl border border-gray-100 bg-gray-50/60 p-1">
                      {clientsLoading ? (
                        <p className="text-xs text-gray-400 px-2 py-2">Загрузка клиентов...</p>
                      ) : matchingClients.length > 0 ? (
                        matchingClients.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => selectExistingClient(client)}
                            className="w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-white transition"
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-gray-700 truncate">{client.name}</span>
                              <span className="block text-xs text-gray-400 truncate">{client.phone || "телефон не указан"}</span>
                            </span>
                            <span className="text-xs text-primary font-medium shrink-0">Выбрать</span>
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 px-2 py-2">Клиент не найден</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Заметка</label>
              <textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm((form) => ({ ...form, notes: e.target.value }))}
                rows={2}
                placeholder="Комментарий к записи"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white resize-none focus:outline-none focus:border-primary/50"
              />
            </div>

            {createError && <p className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{createError}</p>}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={createMutation.isPending || activeServices.length === 0}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 transition disabled:opacity-50"
              >
                {createMutation.isPending ? "Создаём..." : "Создать запись"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError("");
                }}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
              >
                Отмена
              </button>
            </div>
          </form>
        )}

        {!isToday && (
          <button onClick={() => setSelectedDate(today)} className="w-full text-xs py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition font-medium">
            ↩ Вернуться на сегодня
          </button>
        )}

        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-gray-900">День по времени</h2>
              <p className="mt-0.5 text-xs text-gray-400">
                Свободные окна считаются по выбранной услуге.
              </p>
            </div>
            <select
              value={selectedScheduleServiceId}
              onChange={(event) => setSelectedScheduleServiceId(event.target.value)}
              disabled={activeServices.length === 0}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:border-primary/50 sm:w-56"
            >
              {activeServices.length === 0 ? (
                <option value="">Нет активных услуг</option>
              ) : (
                activeServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))
              )}
            </select>
          </div>
          {scheduleSlotsLoading && (
            <p className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-400">
              Обновляем свободные окна...
            </p>
          )}
        </div>

        {isLoading && <div className="text-center py-10 text-gray-400">Загрузка расписания...</div>}

        {!isLoading && !scheduleSlotsLoading && scheduleItems.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center">
            <p className="text-4xl mb-3">📅</p>
            <p className="text-gray-600 font-semibold">На этот день нет доступных окон</p>
            <p className="text-sm text-gray-400 mt-1">
              Проверьте рабочее время, временные закрытия или выберите другую услугу.
            </p>
          </div>
        )}

        <div className="space-y-2">
          {scheduleItems.map((item) => {
            if (item.kind === "free") {
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() =>
                    openCreateAppointment({
                      date: selectedDate,
                      time: item.timeStr,
                      serviceId: selectedScheduleServiceId,
                    })
                  }
                  className="grid w-full grid-cols-[4rem_minmax(0,1fr)] gap-2 text-left"
                >
                  <div className="pt-2 text-right">
                    <p className="text-sm font-bold tabular-nums text-primary">{item.timeStr}</p>
                    <p className="text-[11px] tabular-nums text-gray-300">{item.endTimeStr}</p>
                  </div>
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/70 px-3 py-2.5 transition hover:border-emerald-300 hover:bg-emerald-50">
                    <div className="flex min-w-0 items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-emerald-700">Свободно</span>
                      <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm">
                        Добавить запись
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-emerald-700/70">
                      {selectedScheduleService?.name ?? "Выбранная услуга"}
                    </p>
                  </div>
                </button>
              );
            }

            const a = item.appointment;
            const actionAvailability = getAppointmentActionAvailability(a);
            const isRescheduling = reschedulingAppt?.id === a.id;
            const isReturnReminder = returnReminderAppt?.id === a.id;
            const panelOpen = completingId === a.id || cancelConfirmId === a.id || noShowConfirmId === a.id || isRescheduling || notifyingId === a.id || isReturnReminder;
            const isFocused = highlightedAppointmentId === a.id;
            const statusMeta = getMasterVisibleAppointmentStatusMeta(a.status);
            const hasAvailableActions =
              actionAvailability.canComplete ||
              actionAvailability.canReschedule ||
              actionAvailability.canContact ||
              actionAvailability.canMarkNoShow ||
              actionAvailability.canCancel ||
              actionAvailability.canSetReturnReminder;
            const disclosureState = getAppointmentCardDisclosureState({
              appointmentId: a.id,
              expandedAppointmentIds,
              hasAvailableActions,
              panelOpen,
            });

            return (
              <div key={a.id} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-2">
                <div className="pt-3 text-right">
                  <p className="text-sm font-bold tabular-nums text-gray-700">{fmtTime(a.startsAt, businessTimeZone)}</p>
                  <p className="text-[11px] tabular-nums text-gray-300">{fmtTime(a.endsAt, businessTimeZone)}</p>
                </div>
              <div
                ref={isFocused ? focusedAppointmentRef : undefined}
                className={`min-w-0 w-full overflow-hidden rounded-2xl border border-l-4 border-gray-100 bg-white shadow-sm transition sm:max-w-[34rem] ${statusMeta?.borderClass ?? "border-l-gray-100"} ${isFocused ? "ring-2 ring-primary/40 ring-offset-2" : ""}`}
              >
                <div
                  onClick={(event) => {
                    if (hasAvailableActions) handleAppointmentCardClick(event, a.id);
                  }}
                  className={`p-3 ${hasAvailableActions ? "cursor-pointer" : ""}`}
                >
                  <div className="mb-2 flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0 space-y-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="text-[15px] font-bold tabular-nums leading-tight text-gray-900">
                          {fmtTime(a.startsAt, businessTimeZone)} <span className="text-gray-300">→</span> {fmtTime(a.endsAt, businessTimeZone)}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-gray-500">
                          <span aria-hidden="true">⏱</span>
                          {a.service.durationMin} мин
                        </span>
                      </div>
                      <p className="min-w-0 break-words text-sm font-semibold leading-snug text-gray-800">{a.service.name}</p>
                    </div>
                    <div className="flex shrink-0 items-start gap-1.5">
                      <div className="flex flex-col items-end gap-1 text-right">
                        {a.status === "COMPLETED" && a.actualPrice !== null ? (
                          <span className="text-sm font-bold leading-tight text-green-600">{fmtPrice(a.actualPrice)}</span>
                        ) : (
                          <span className="text-sm leading-tight text-gray-500">{priceRange(a.service)}</span>
                        )}
                        <div className="flex flex-wrap justify-end gap-1">
                          {statusMeta && (
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusMeta.badgeClass}`}>
                              {statusMeta.label}
                            </span>
                          )}
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                            {SOURCE_LABEL[a.source] ?? a.source}
                          </span>
                        </div>
                      </div>
                      {hasAvailableActions && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleAppointmentCard(a.id);
                          }}
                          aria-expanded={disclosureState.isExpanded}
                          aria-label={disclosureState.toggleLabel}
                          className="grid h-6 w-6 place-items-center rounded-full bg-gray-50 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                        >
                          <span
                            aria-hidden="true"
                            className={`text-sm leading-none transition-transform ${disclosureState.isExpanded ? "rotate-180" : ""}`}
                          >
                            ⌄
                          </span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex min-w-0 items-center gap-2.5">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                      {a.client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-600">
                      <span className="min-w-0 break-words">{a.client.name}</span>
                      <a href={`tel:${a.client.phone}`} className="text-primary text-xs font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                        {a.client.phone}
                      </a>
                    </div>
                  </div>

                  {a.notes && (
                    <p className="text-xs text-gray-400 mt-2 italic bg-gray-50 rounded-lg px-2 py-1">{a.notes}</p>
                  )}
                </div>

                {/* ─── Inline: Complete ─── */}
                {completingId === a.id && (
                  <div className="border-t border-gray-100 bg-blue-50/40 p-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Фактическая оплата:</p>
                    <div className="flex gap-2 items-center mb-2">
                      <input type="number" value={actualPriceInput} onChange={(e) => setActualPriceInput(e.target.value)}
                        placeholder="Сумма в ₽" className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white" autoFocus />
                      <span className="text-sm text-gray-500">₽</span>
                    </div>
                    <div className="flex gap-1.5 mb-2">
                      <button onClick={() => setActualPriceInput(String(Math.round(a.service.priceFrom / 100)))} className="text-xs px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50">
                        По прайсу ({priceRange(a.service)})
                      </button>
                      <button onClick={() => setActualPriceInput("0")} className="text-xs px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50">
                        Без оплаты
                      </button>
                    </div>
                    <ReturnReminderDaysPicker
                      appointment={a}
                      businessTimeZone={businessTimeZone}
                      daysInput={returnReminderDaysInput}
                      onDaysInputChange={setReturnReminderDaysInput}
                      className="mb-2 border-t border-blue-100 pt-2"
                    />
                    <div className="flex gap-1.5">
                      <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Отмена</button>
                      <button onClick={() => confirmComplete(a)} disabled={patchMutation.isPending} className="flex-1 text-xs py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50">
                        {patchMutation.isPending ? "..." : "✓ Завершить"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Inline: Reschedule ─── */}
                {isRescheduling && (
                  <div className="border-t border-gray-100 bg-yellow-50/40 p-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Новая дата:</p>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleTime(""); }}
                      className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white mb-2"
                    />

                    {/* Slot picker */}
                    {rescheduleDate && (
                      <div className="mb-2">
                        {slotsLoading ? (
                          <p className="text-xs text-gray-400 py-1">Загрузка слотов...</p>
                        ) : visibleAvailableSlots.length > 0 ? (
                          <>
                            <p className="text-[11px] text-gray-400 mb-1.5">Свободные слоты:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {visibleAvailableSlots.map((slot) => (
                                <button
                                  key={slot.timeStr}
                                  onClick={() => setRescheduleTime(slot.timeStr)}
                                  className={`text-xs px-2.5 py-1 rounded-lg border transition font-medium ${
                                    rescheduleTime === slot.timeStr
                                      ? "bg-yellow-400 border-yellow-400 text-white"
                                      : "bg-white border-gray-200 text-gray-700 hover:border-yellow-300 hover:bg-yellow-50"
                                  }`}
                                >
                                  {slot.timeStr}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-2.5 py-1.5">
                            Нет свободных слотов на этот день
                          </p>
                        )}

                        {/* Manual time override */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                          <p className="text-[11px] text-gray-400 shrink-0">Другое время:</p>
                          <input
                            type="time"
                            value={rescheduleTime}
                            onChange={(e) => setRescheduleTime(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2.5 py-1 text-xs bg-white w-24"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-1.5 mt-1">
                      <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Отмена</button>
                      <button
                        onClick={() => confirmReschedule(a.id)}
                        disabled={rescheduleMutation.isPending || !rescheduleDate || !rescheduleTime}
                        className="flex-1 text-xs py-2 rounded-lg bg-yellow-500 text-white font-medium hover:bg-yellow-600 disabled:opacity-50"
                      >
                        {rescheduleMutation.isPending ? "..." : "🔄 Перенести"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Inline: Notify ─── */}
                {notifyingId === a.id && (
                  <div className="border-t border-gray-100 bg-purple-50/40 p-3">
                    {notifyResult === "ok" ? (
                      <p className="text-center text-green-600 text-sm py-2 font-medium">✓ Сообщение отправлено</p>
                    ) : notifyResult === "no_channel" ? (
                      <div>
                        <p className="text-xs text-red-500 mb-2 font-medium">Нет Telegram/MAX — позвоните напрямую:</p>
                        <a href={`tel:${a.client.phone}`} className="block text-center text-primary font-semibold text-base py-1">{a.client.phone}</a>
                        <button onClick={closeAll} className="w-full mt-2 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Закрыть</button>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-gray-600 mb-2">Сообщение клиенту:</p>
                        <div className="flex flex-col gap-1 mb-2">
                          {[
                            `Напоминаем о вашей записи в ${fmtTime(a.startsAt, businessTimeZone)}. Вы в пути? 😊`,
                            `Ваша запись началась в ${fmtTime(a.startsAt, businessTimeZone)}. Всё ещё ждём вас!`,
                            "Всё в силе? Пожалуйста, подтвердите что придёте.",
                          ].map((preset) => (
                            <button key={preset} onClick={() => setNotifyMessage(preset)}
                              className={`text-xs text-left px-2.5 py-1.5 rounded-lg border transition ${notifyMessage === preset ? "border-purple-300 bg-purple-100 text-purple-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                              {preset}
                            </button>
                          ))}
                        </div>
                        <textarea value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)}
                          rows={2} className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white resize-none mb-2 focus:outline-none focus:border-purple-300"
                          placeholder="Или введите своё сообщение..." />
                        <div className="flex gap-1.5">
                          <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Отмена</button>
                          <button onClick={() => notifyMutation.mutate({ id: a.id, message: notifyMessage })}
                            disabled={notifyMutation.isPending || !notifyMessage.trim()}
                            className="flex-1 text-xs py-2 rounded-lg bg-purple-500 text-white font-medium hover:bg-purple-600 disabled:opacity-50">
                            {notifyMutation.isPending ? "..." : "📨 Отправить"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ─── Inline: No-show confirm ─── */}
                {noShowConfirmId === a.id && (
                  <div className="border-t border-gray-100 bg-orange-50/40 p-3">
                    <p className="text-xs text-gray-700 mb-1">
                      Отметить, что {a.client.name} не пришёл на {fmtTime(a.startsAt, businessTimeZone)}?
                    </p>
                    <p className="mb-2 text-[11px] text-gray-500">
                      Запись уйдёт в отдельный блок неявок. Клиенту не отправляем резкое сообщение автоматически.
                    </p>
                    <div className="flex gap-1.5">
                      <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Нет</button>
                      <button
                        onClick={() => patchMutation.mutate({ id: a.id, body: { status: "NO_SHOW" } })}
                        disabled={patchMutation.isPending}
                        className="flex-1 text-xs py-2 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-50"
                      >
                        Да, не пришёл
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Inline: Return reminder ─── */}
                {isReturnReminder && (
                  <div className="border-t border-gray-100 bg-green-50/40 p-3">
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      Когда напомнить клиенту вернуться?
                    </p>
                    <p className="text-[11px] text-gray-500 mb-2">
                      Напоминание относится только к этому клиенту и этой завершённой записи.
                    </p>
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {[
                        { label: "Через 2 недели", days: 14 },
                        { label: "Через 3 недели", days: 21 },
                        { label: "Через месяц", days: 30 },
                      ].map((option) => (
                        <button
                          key={option.days}
                          type="button"
                          onClick={() => setReturnReminderAfter(a, option.days)}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="date"
                      value={returnReminderDate}
                      onChange={(event) => setReturnReminderDate(event.target.value)}
                      className="mb-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm"
                    />
                    <div className="flex gap-1.5">
                      <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Отмена</button>
                      <button
                        onClick={() => confirmReturnReminder(a.id)}
                        disabled={patchMutation.isPending || !returnReminderDate}
                        className="flex-1 text-xs py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
                      >
                        {patchMutation.isPending ? "..." : "Сохранить"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Inline: Cancel confirm ─── */}
                {cancelConfirmId === a.id && (
                  <div className="border-t border-gray-100 bg-red-50/40 p-3">
                    <p className="text-xs text-gray-600 mb-2">Отменить запись {a.client.name} на {fmtTime(a.startsAt, businessTimeZone)}?</p>
                    <p className="mb-2 text-[11px] text-gray-500">
                      Если у клиента подключены уведомления, отправим сообщение об отмене.
                    </p>
                    <div className="flex gap-1.5">
                      <button onClick={closeAll} className="flex-1 text-xs py-2 rounded-lg bg-white border border-gray-200 text-gray-500">Нет</button>
                      <button onClick={() => cancelMutation.mutate(a.id)} disabled={cancelMutation.isPending} className="flex-1 text-xs py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50">
                        Да, отменить
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Action buttons ─── */}
                {(actionAvailability.canComplete ||
                  actionAvailability.canReschedule ||
                  actionAvailability.canContact ||
                  actionAvailability.canMarkNoShow ||
                  actionAvailability.canCancel) && disclosureState.showActionButtons && (
                  <div className="border-t border-gray-50 px-3 py-2 space-y-1.5">
                    <div className="flex gap-1.5">
                      {actionAvailability.canComplete && (
                        <button onClick={() => startComplete(a)} className="flex-1 text-xs py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition font-medium">
                          ✓ Завершить
                        </button>
                      )}
                      {actionAvailability.canReschedule && (
                        <button onClick={() => startReschedule(a)} className="flex-1 text-xs py-1.5 rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition font-medium">
                          🔄 Перенести
                        </button>
                      )}
                      {actionAvailability.canContact && (
                        <button onClick={() => startNotify(a)} className="flex-1 text-xs py-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition font-medium">
                          📨 Написать
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      {actionAvailability.canMarkNoShow && (
                        <button onClick={() => setNoShowConfirmId(a.id)} disabled={patchMutation.isPending}
                          className="flex-1 text-xs py-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition font-medium">
                          ✗ Не пришёл
                        </button>
                      )}
                      {actionAvailability.canCancel && (
                        <button onClick={() => setCancelConfirmId(a.id)} className="flex-1 text-xs py-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition font-medium">
                          Отменить
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {actionAvailability.canSetReturnReminder && disclosureState.showActionButtons && (
                  <div className="border-t border-gray-50 px-3 py-2">
                    <button onClick={() => startReturnReminder(a)} className="w-full text-xs py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition font-medium">
                      Напомнить вернуться
                    </button>
                  </div>
                )}
              </div>
              </div>
            );
          })}
        </div>

        {hiddenAppointments.length > 0 && (
          <details className="mt-2" open={visibleAppointments.length === 0}>
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none py-1">
              Отменённые и не пришедшие ({hiddenAppointments.length})
            </summary>
            <div className="mt-2 space-y-1.5">
              {hiddenAppointments.map((a) => {
                const statusMeta = getMasterVisibleAppointmentStatusMeta(a.status);
                return (
                  <div key={a.id} className={`bg-white rounded-xl border border-gray-100 p-3 border-l-4 ${statusMeta?.borderClass ?? "border-l-gray-100"} opacity-60`}>
                    <p className="text-sm text-gray-600">
                      {fmtTime(a.startsAt, businessTimeZone)} · <span className="font-medium">{a.service.name}</span> · {a.client.name}
                    </p>
                    {statusMeta && <p className="text-xs text-gray-400 mt-0.5">{statusMeta.label}</p>}
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
